import { useEffect, useRef } from 'react';
import { track } from '../lib/analytics';

/**
 * Hook to track when a section becomes visible.
 * Fires section_viewed event once per session when the section is ≥50% visible.
 *
 * @param {string} section - Section name (e.g., 'hero', 'api_features')
 * @param {number} index - Position index of the section in the page
 * @returns {object} Ref to attach to the section element
 */
export function useSectionView(section, index) {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          track('section_viewed', {
            section,
            position_index: index
          });
          // Disconnect after first view (once per session)
          observer.disconnect();
        }
      },
      { threshold: 0.5 } // Fire when 50% visible
    );

    observer.observe(el);

    return () => observer.disconnect();
  }, [section, index]);

  return ref;
}
